// Receipt Template Schema - Core Types

export type Alignment = 'left' | 'center' | 'right';
export type PaperStyle = 'thermal' | 'plain' | 'new' | 'ruffled' | 'used' | 'worn' | 'old';
export type DividerStyle = 'dashed' | 'solid' | 'dotted' | 'double' | 'equals' | 'dots' | 'colons' | 'stars' | 'spaces' | 'none';
export type BarcodeType = 'code128' | 'code39' | 'ean13' | 'upc' | 'qr';
export type CurrencyFormat = 'none' | '$' | '€' | '£' | '¥' | '₹';
export type SpacingStyle = 'spaced' | 'narrow';

// Settings for the entire receipt
export interface ReceiptSettings {
  width: number;
  font: string;
  fontSize: number;
  paperStyle: PaperStyle;
  backgroundColor: string;
  textColor: string;
  showWatermark: boolean;
  watermarkText?: string;
  currency: CurrencyFormat;
  currencyPosition: 'before' | 'after';
  spacing?: SpacingStyle;
}

// Header Section Config
export interface HeaderConfig {
  alignment: Alignment;
  logoUrl?: string;
  logoData?: string; // Base64 encoded image data for uploaded logos
  logoWidth: number;
  logoHeight: number;
  businessName: string;
  businessNameFontSize: number;
  addressLines: string[];
  showDivider: boolean;
  dividerStyle: DividerStyle;
}

// Two Column Information Section Config
export interface TwoColumnItem {
  id: string;
  label: string;
  value: string;
}

export interface TwoColumnConfig {
  alignment: Alignment;
  column1: TwoColumnItem[];
  column2: TwoColumnItem[];
  showDivider: boolean;
  dividerStyle: DividerStyle;
}

// Items List Section Config
export interface ItemRow {
  id: string;
  quantity?: string; // String to allow custom formatting (commas, etc.)
  name: string;
  price: string; // String to allow custom formatting
  sku?: string; // Optional SKU/barcode number
}

export interface ItemsListConfig {
  alignment: Alignment;
  showQuantity: boolean;
  showPrice?: boolean; // Whether to show price column
  showSku?: boolean; // Whether to show SKU column
  showTotal?: boolean; // Whether to show total column (qty x price)
  quantityPosition?: 'left' | 'right'; // Default: 'left'
  showHeader?: boolean; // Default: false
  showHeaderDivider?: boolean; // Default: false
  headerLabels?: {
    quantity: string;
    item: string;
    amount: string;
  };
  items: ItemRow[];
  showTopDivider?: boolean; // Default: false
  showDivider: boolean;
  dividerStyle: DividerStyle;
}

// Totals Section Config
export interface TotalLine {
  id: string;
  label: string;
  value: string;  // String to preserve exact formatting (e.g., "0.00", "25.71")
  isBold?: boolean;
  isLarge?: boolean;
  isInverted?: boolean;  // Black background, white text (like Costco TOTAL)
}

export interface TotalsConfig {
  alignment: Alignment; // Kept for compatibility
  labelPosition: number; // 0-100, percentage offset from left (0=left, 50=center, 100=right)
  labelAlignment?: Alignment; // Alignment for labels only (left/center/right), values stay fixed
  lines: TotalLine[];
  increaseTotalSize: boolean;
  totalSizeIncrease: number; // percentage like 10, 20, 30, 40, 50
  showDivider: boolean;
  dividerStyle: DividerStyle;
}

// Payment Section Config
export interface PaymentField {
  id: string;
  label: string;
  value: string;
}

export interface PaymentConfig {
  alignment: Alignment;
  method?: string; // Simple payment method string (e.g., "VISA ****4144")
  paymentType?: 'card' | 'cash';
  cardFields?: PaymentField[];
  cashFields?: PaymentField[];
  fields?: PaymentField[]; // Generic fields array
  showDivider: boolean;
  dividerStyle: DividerStyle;
}

// Date & Time Section Config
export interface DateTimeConfig {
  alignment: Alignment;
  format: string;
  dateTime: string;
  showDivider: boolean;
  dividerStyle: DividerStyle;
}

// Custom Message Section Config
export interface CustomMessageConfig {
  alignment: Alignment;
  message: string;
  fontSize: number;
  isBold?: boolean;
  showDivider: boolean;
  dividerStyle: DividerStyle;
}

// Barcode Section Config
export interface BarcodeConfig {
  alignment: Alignment;
  type: BarcodeType;
  value: string;
  showValue: boolean;
  width: number;
  height: number;
  showDivider: boolean;
  dividerStyle: DividerStyle;
}

// Divider Section Config
export interface DividerConfig {
  style: DividerStyle;
  spacing: number;
}

// Union type for all section configs
export type SectionConfig =
  | HeaderConfig
  | TwoColumnConfig
  | ItemsListConfig
  | TotalsConfig
  | PaymentConfig
  | DateTimeConfig
  | CustomMessageConfig
  | BarcodeConfig
  | DividerConfig;

// Section type literals
export type SectionType =
  | 'header'
  | 'two-column'
  | 'items-list'
  | 'totals'
  | 'payment'
  | 'datetime'
  | 'custom-message'
  | 'barcode'
  | 'divider';

// Generic section interface with discriminated union
export interface BaseSection {
  id: string;
  enabled: boolean;
}

export interface HeaderSection extends BaseSection {
  type: 'header';
  config: HeaderConfig;
}

export interface TwoColumnSection extends BaseSection {
  type: 'two-column';
  config: TwoColumnConfig;
}

export interface ItemsListSection extends BaseSection {
  type: 'items-list';
  config: ItemsListConfig;
}

export interface TotalsSection extends BaseSection {
  type: 'totals';
  config: TotalsConfig;
}

export interface PaymentSection extends BaseSection {
  type: 'payment';
  config: PaymentConfig;
}

export interface DateTimeSection extends BaseSection {
  type: 'datetime';
  config: DateTimeConfig;
}

export interface CustomMessageSection extends BaseSection {
  type: 'custom-message';
  config: CustomMessageConfig;
}

export interface BarcodeSection extends BaseSection {
  type: 'barcode';
  config: BarcodeConfig;
}

export interface DividerSection extends BaseSection {
  type: 'divider';
  config: DividerConfig;
}

// Union type for all sections
export type ReceiptSection =
  | HeaderSection
  | TwoColumnSection
  | ItemsListSection
  | TotalsSection
  | PaymentSection
  | DateTimeSection
  | CustomMessageSection
  | BarcodeSection
  | DividerSection;

// Main Receipt Template Interface
export interface ReceiptTemplate {
  id: string;
  name: string;
  brand?: string;
  description?: string;
  thumbnail?: string;
  settings: ReceiptSettings;
  sections: ReceiptSection[];
  createdAt?: string;
  updatedAt?: string;
}

// Helper type for section type to config mapping
export type SectionConfigMap = {
  header: HeaderConfig;
  'two-column': TwoColumnConfig;
  'items-list': ItemsListConfig;
  totals: TotalsConfig;
  payment: PaymentConfig;
  datetime: DateTimeConfig;
  'custom-message': CustomMessageConfig;
  barcode: BarcodeConfig;
  divider: DividerConfig;
};

// Section metadata for the builder
export interface SectionMeta {
  type: SectionType;
  label: string;
  icon: string;
  description: string;
}

export const SECTION_METADATA: SectionMeta[] = [
  { type: 'header', label: 'Header', icon: '🏪', description: 'Brand logo, name, and address' },
  { type: 'two-column', label: 'Two Column Info', icon: '📋', description: 'Key-value pairs' },
  { type: 'items-list', label: 'Items List', icon: '📦', description: 'Product items with prices' },
  { type: 'totals', label: 'Totals', icon: '💰', description: 'Subtotal, tax, and total' },
  { type: 'payment', label: 'Payment', icon: '💳', description: 'Card or cash payment info' },
  { type: 'datetime', label: 'Date & Time', icon: '📅', description: 'Transaction timestamp' },
  { type: 'custom-message', label: 'Custom Message', icon: '📝', description: 'Return policy, notes' },
  { type: 'barcode', label: 'Barcode', icon: '📊', description: 'Barcode or QR code' },
  { type: 'divider', label: 'Divider', icon: '➖', description: 'Horizontal line separator' },
];

